'use client'

import { USER_ONBOARDED } from 'src/constants/session'

const persistentItems = [USER_ONBOARDED]

// These helpers close over nothing but `window`, so they live at module scope and
// keep a stable identity across renders. Returning fresh closures from the hook
// invalidated every downstream useCallback/useMemo/useEffect that depends on them.
const isStorageReady = () => (typeof window !== 'undefined')

const getItem = (key: string): string | null => {
  if (!isStorageReady()) {
    return null
  }
  let storedValue = null
  try {
    storedValue = localStorage.getItem(key) || ''
  }
  catch (error) {
    console.error(`Error retrieving item with key "${key}":`, error)
  }
  return storedValue
}

const setItem = (key: string, value: string | unknown): void => {
  if (!isStorageReady()) {
    return
  }
  if (typeof value !== 'string') {
    try {
      localStorage.setItem(key, JSON.stringify(value) as string)
    }
    catch (error) {
      console.error(`Error stringifying value for key "${key}":`, error)
      return
    }
  }
  else {
    localStorage.setItem(key, value)
  }
}

const removeItem = (key: string): void => {
  if (!isStorageReady()) {
    return
  }
  localStorage.removeItem(key)
}

const clearAll = (): void => {
  if (!isStorageReady()) {
    return
  }
  const keys = Object.keys(localStorage)
  keys.forEach((key) => {
    if (!persistentItems.includes(key)) {
      localStorage.removeItem(key)
    }
  })
}

const getAllItems = (): Record<string, string> | null => {
  if (!isStorageReady()) {
    return null
  }
  const items: Record<string, string> = {}
  Object.keys(localStorage).forEach((key) => {
    items[key] = localStorage.getItem(key) || ''
  })
  return items
}

const storage = {
  getItem,
  setItem,
  removeItem,
  clearAll,
  getAllItems,
} as const

const useAppStorage = () => storage

export default useAppStorage
